<template>
  <AdminLayout>
    <!-- Barre fixe : identité + actions. Jamais recouverte, toujours accessible
         même avec un circuit long qui défile en dessous. -->
    <div class="sticky top-0 z-20 bg-white border-b border-gray-200 shadow-sm">
      <div class="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-start gap-4">
        <button
          @click="router.push('/workflow-templates')"
          class="p-2 hover:bg-gray-100 rounded-lg transition-colors shrink-0 mt-1"
          title="Retour à la liste"
        >
          <ArrowLeft class="w-5 h-5 text-gray-500" />
        </button>

        <div class="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label class="block text-xs font-medium text-gray-500 mb-1">Nom du workflow <span class="text-red-500">*</span></label>
            <input
              v-model="form.nom"
              type="text"
              placeholder="ex: Note à l'attention du SG"
              class="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-medium focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/20"
            />
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-500 mb-1">Description</label>
            <input
              v-model="form.description"
              type="text"
              placeholder="Description optionnelle..."
              class="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/20"
            />
          </div>
        </div>

        <div class="flex items-center gap-2 shrink-0 mt-1">
          <label class="flex items-center gap-2 cursor-pointer px-3 py-2 border border-gray-200 rounded-lg">
            <input type="checkbox" v-model="form.is_active" class="rounded" />
            <span class="text-sm text-gray-700">Actif</span>
          </label>
          <button
            v-if="templateId"
            @click="doDuplicate"
            class="p-2.5 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            title="Dupliquer ce workflow"
          >
            <Copy class="w-4 h-4 text-gray-500" />
          </button>
          <button
            @click="save"
            :disabled="saving"
            class="px-4 py-2.5 bg-[#22282F] text-white rounded-lg text-sm hover:bg-[#22282F]/90 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            <div v-if="saving" class="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
            Enregistrer
          </button>
        </div>
      </div>
    </div>

    <div class="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div v-if="loading" class="flex items-center justify-center py-16">
        <div class="w-8 h-8 border-4 border-gray-200 border-t-[#22282F] rounded-full animate-spin" />
      </div>

      <template v-else>
        <!-- ============================================================
             CANEVAS DU CIRCUIT — défilement horizontal explicite, jamais
             de contenu coupé : chaque étape garde sa largeur complète.
        ============================================================= -->
        <div class="bg-white border border-gray-200 rounded-xl shadow-sm mb-6">
          <div class="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
            <h3 class="text-sm font-semibold text-[#22282F] flex items-center gap-2">
              <Workflow class="w-4 h-4 text-gray-400" />
              Étapes du circuit
            </h3>
            <span class="text-xs text-gray-400">{{ steps.length }} étape(s) · faites défiler horizontalement →</span>
          </div>

          <div class="overflow-x-auto">
            <div class="flex items-stretch gap-0 p-5 min-w-max">
              <template v-for="(step, idx) in steps" :key="step._key">
                <!-- Carte étape -->
                <div class="w-72 shrink-0 border border-gray-200 rounded-xl p-4 flex flex-col gap-3 bg-gray-50/50">
                  <div class="flex items-center justify-between">
                    <span class="w-6 h-6 rounded-full bg-[#22282F] text-white text-xs font-bold flex items-center justify-center">{{ idx + 1 }}</span>
                    <div class="flex items-center gap-1">
                      <button :disabled="idx === 0" @click="moveStep(idx, -1)" class="p-1.5 rounded hover:bg-gray-200 disabled:opacity-30 disabled:hover:bg-transparent" title="Déplacer vers la gauche">
                        <ChevronLeft class="w-4 h-4 text-gray-500" />
                      </button>
                      <button :disabled="idx === steps.length - 1" @click="moveStep(idx, 1)" class="p-1.5 rounded hover:bg-gray-200 disabled:opacity-30 disabled:hover:bg-transparent" title="Déplacer vers la droite">
                        <ChevronRight class="w-4 h-4 text-gray-500" />
                      </button>
                      <button @click="removeStep(idx)" class="p-1.5 rounded hover:bg-red-50" title="Retirer l'étape">
                        <Trash2 class="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  </div>

                  <!-- Nature de l'étape -->
                  <div class="grid grid-cols-3 gap-1 bg-gray-100 rounded-lg p-1">
                    <button
                      v-for="opt in kindOptions"
                      :key="opt.value"
                      @click="setKind(step, opt.value)"
                      :class="[
                        'py-1.5 rounded-md text-[11px] font-medium transition-colors leading-tight',
                        step.kind === opt.value ? 'bg-white text-[#22282F] shadow-sm' : 'text-gray-500 hover:text-gray-700'
                      ]"
                    >
                      {{ opt.label }}
                    </button>
                  </div>

                  <!-- FIXED_ENTITY -->
                  <div v-if="step.kind === 'FIXED_ENTITY'" class="space-y-2">
                    <select v-model="step.target_type" @change="onTargetTypeChange(step)" class="w-full px-2.5 py-2 border border-gray-200 rounded-lg text-xs bg-white focus:outline-none focus:border-[#22282F]">
                      <option v-for="t in TARGET_TYPE_OPTIONS" :key="t.value" :value="t.value">{{ t.label }}</option>
                    </select>

                    <select v-if="step.target_type === 'DIRECTION'" v-model.number="step.target_id" class="w-full px-2.5 py-2 border border-gray-200 rounded-lg text-xs bg-white focus:outline-none focus:border-[#22282F]">
                      <option :value="null">-- Choisir --</option>
                      <option v-for="d in directions" :key="d.id" :value="d.id">{{ d.code }} — {{ d.nom }}</option>
                    </select>

                    <template v-if="step.target_type === 'DEPARTEMENT'">
                      <select v-model.number="step._directionFilter" @change="step.target_id = null" class="w-full px-2.5 py-2 border border-gray-200 rounded-lg text-xs bg-white focus:outline-none focus:border-[#22282F]">
                        <option :value="null">-- Direction --</option>
                        <option v-for="d in directions" :key="d.id" :value="d.id">{{ d.code }} — {{ d.nom }}</option>
                      </select>
                      <select v-model.number="step.target_id" :disabled="!step._directionFilter" class="w-full px-2.5 py-2 border border-gray-200 rounded-lg text-xs bg-white focus:outline-none focus:border-[#22282F] disabled:bg-gray-100">
                        <option :value="null">-- Département --</option>
                        <option v-for="d in departementsForDirection(step._directionFilter)" :key="d.id" :value="d.id">{{ d.nom }}</option>
                      </select>
                    </template>

                    <template v-if="step.target_type === 'SERVICE'">
                      <select v-model.number="step._directionFilter" @change="step._departementFilter = null; step.target_id = null" class="w-full px-2.5 py-2 border border-gray-200 rounded-lg text-xs bg-white focus:outline-none focus:border-[#22282F]">
                        <option :value="null">-- Direction --</option>
                        <option v-for="d in directions" :key="d.id" :value="d.id">{{ d.code }} — {{ d.nom }}</option>
                      </select>
                      <select v-model.number="step._departementFilter" :disabled="!step._directionFilter" @change="step.target_id = null" class="w-full px-2.5 py-2 border border-gray-200 rounded-lg text-xs bg-white focus:outline-none focus:border-[#22282F] disabled:bg-gray-100">
                        <option :value="null">-- Département --</option>
                        <option v-for="d in departementsForDirection(step._directionFilter)" :key="d.id" :value="d.id">{{ d.nom }}</option>
                      </select>
                      <select v-model.number="step.target_id" :disabled="!step._departementFilter" class="w-full px-2.5 py-2 border border-gray-200 rounded-lg text-xs bg-white focus:outline-none focus:border-[#22282F] disabled:bg-gray-100">
                        <option :value="null">-- Service --</option>
                        <option v-for="s in servicesForDepartement(step._departementFilter)" :key="s.id" :value="s.id">{{ s.nom }}</option>
                      </select>
                    </template>

                    <select v-if="step.target_type === 'AMBASSADE'" v-model.number="step.target_id" class="w-full px-2.5 py-2 border border-gray-200 rounded-lg text-xs bg-white focus:outline-none focus:border-[#22282F]">
                      <option :value="null">-- Choisir --</option>
                      <option v-for="a in ambassades" :key="a.id" :value="a.id">{{ a.code }} — {{ a.nom }}</option>
                    </select>

                    <select v-if="step.target_type === 'USER'" v-model.number="step.target_id" class="w-full px-2.5 py-2 border border-gray-200 rounded-lg text-xs bg-white focus:outline-none focus:border-[#22282F]">
                      <option :value="null">-- Choisir --</option>
                      <option v-for="u in users" :key="u.id" :value="u.id">{{ u.full_name }} ({{ u.role_label }})</option>
                    </select>
                  </div>

                  <!-- HIERARCHIE_INITIATEUR -->
                  <div v-else-if="step.kind === 'HIERARCHIE_INITIATEUR'" class="flex gap-2 bg-violet-50 border border-violet-100 rounded-lg p-2.5">
                    <GitBranch class="w-4 h-4 text-violet-500 shrink-0 mt-0.5" />
                    <p class="text-[11px] text-violet-700 leading-snug">
                      Au moment de la rédaction, remonte automatiquement du rédacteur jusqu'à son
                      plafond (Directeur, DC ou Ambassadeur/Chargé d'Affaires selon son rattachement).
                      S'adapte à n'importe quel initiateur — aucune cible à choisir ici.
                    </p>
                  </div>

                  <!-- ROLE_NATIONAL -->
                  <div v-else-if="step.kind === 'ROLE_NATIONAL'" class="space-y-2">
                    <select v-model="step.role_national_code" class="w-full px-2.5 py-2 border border-gray-200 rounded-lg text-xs bg-white focus:outline-none focus:border-[#22282F]">
                      <option :value="null">-- Choisir un rôle --</option>
                      <option v-for="r in ROLE_NATIONAL_OPTIONS" :key="r.code" :value="r.code">{{ r.label }}</option>
                    </select>
                    <div class="flex gap-2 bg-amber-50 border border-amber-100 rounded-lg p-2.5">
                      <Shield class="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                      <p class="text-[11px] text-amber-700 leading-snug">
                        Résolu par rôle, pas par personne : reste valide après un changement de titulaire.
                      </p>
                    </div>
                    <div
                      v-if="step.role_national_code === 'ASSISTANT_SGA' || step.role_national_code === 'SGA'"
                      class="flex gap-2 bg-blue-50 border border-blue-100 rounded-lg p-2.5"
                    >
                      <Info class="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                      <p class="text-[11px] text-blue-700 leading-snug">
                        Si le SG a désactivé le relais obligatoire (réglage global), cette étape est
                        automatiquement court-circuitée et le circuit va directement au SG.
                      </p>
                    </div>
                  </div>

                  <!-- Type d'instruction + options -->
                  <div class="pt-2 border-t border-gray-200 space-y-2">
                    <select v-model="step.instruction_type" class="w-full px-2.5 py-2 border border-gray-200 rounded-lg text-xs bg-white focus:outline-none focus:border-[#22282F]">
                      <option
                        v-for="opt in INSTRUCTION_TYPE_OPTIONS"
                        :key="opt.value"
                        :value="opt.value"
                        :disabled="opt.value === 'ACTION_CONJOINTE' && step.kind === 'HIERARCHIE_INITIATEUR'"
                      >
                        {{ opt.label }}
                      </option>
                    </select>
                    <label v-if="step.instruction_type === 'ACTION_CONJOINTE'" class="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" v-model="step.is_principal" class="rounded" />
                      <span class="text-xs text-gray-600">Porteur principal du groupe</span>
                    </label>
                    <div class="flex items-center gap-4">
                      <label class="flex items-center gap-1.5 cursor-pointer">
                        <input type="checkbox" v-model="step.requires_paraphe" class="rounded" />
                        <span class="text-xs text-gray-600">Paraphe</span>
                      </label>
                      <label class="flex items-center gap-1.5 cursor-pointer">
                        <input type="checkbox" v-model="step.requires_signature" class="rounded" />
                        <span class="text-xs text-gray-600">Signature</span>
                      </label>
                      <span
                        v-if="idx === steps.length - 1 && !step.requires_paraphe"
                        class="text-xs text-amber-600"
                      >
                        ⚠ Cette étape clôt le circuit (PKI) : « Paraphe » doit rester coché.
                      </span>
                    </div>
                    <p v-if="idx === steps.length - 1" class="text-[11px] text-gray-400 leading-snug">
                      La dernière étape clôt toujours le circuit (PKI), que « Signature » soit coché ou non.
                      « Signature » n'ajoute qu'un tampon visuel — cochez-le, ici ou sur une étape
                      précédente, uniquement si cette personne doit apposer sa propre signature sur le document.
                    </p>
                  </div>
                </div>

                <!-- Connecteur -->
                <div v-if="idx < steps.length - 1" class="flex items-center justify-center w-10 shrink-0">
                  <ArrowRight class="w-5 h-5 text-gray-300" />
                </div>
              </template>

              <!-- Ajouter une étape -->
              <div class="flex items-center pl-3">
                <button
                  @click="addStep"
                  class="w-16 h-16 rounded-xl border-2 border-dashed border-gray-300 hover:border-[#22282F] hover:bg-gray-50 transition-colors flex items-center justify-center shrink-0"
                  title="Ajouter une étape"
                >
                  <Plus class="w-6 h-6 text-gray-400" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- ============================================================
             APERÇU POUR UN UTILISATEUR — vérifie la résolution dynamique
             avant de faire confiance au circuit.
        ============================================================= -->
        <div class="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
          <h3 class="text-sm font-semibold text-[#22282F] flex items-center gap-2 mb-3">
            <Eye class="w-4 h-4 text-gray-400" />
            Aperçu pour un initiateur donné
          </h3>

          <div v-if="!templateId" class="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-4">
            Enregistrez d'abord le workflow pour activer l'aperçu.
          </div>
          <template v-else>
            <div class="flex gap-3 mb-4">
              <select v-model.number="previewUserId" class="flex-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:border-[#22282F]">
                <option :value="null">-- Choisir un utilisateur pour simuler la rédaction --</option>
                <option v-for="u in users" :key="u.id" :value="u.id">{{ u.full_name }} ({{ u.role_label }})</option>
              </select>
              <button
                @click="runPreview"
                :disabled="!previewUserId || previewing"
                class="px-4 py-2.5 border border-gray-200 rounded-lg text-sm hover:bg-gray-50 transition-colors disabled:opacity-50 flex items-center gap-2 shrink-0"
              >
                <div v-if="previewing" class="w-4 h-4 border-2 border-gray-300 border-t-[#22282F] rounded-full animate-spin" />
                Prévisualiser
              </button>
            </div>

            <div v-if="previewError" class="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg p-3">
              {{ previewError }}
            </div>
            <div v-else-if="previewResult" class="overflow-x-auto">
              <div class="flex items-center gap-1.5 min-w-max py-1">
                <template v-for="(item, idx) in previewResult.items" :key="idx">
                  <div class="flex flex-col items-start gap-0.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap border bg-green-50 text-green-700 border-green-100">
                    <span>{{ item.target_label }}</span>
                    <span class="text-[10px] text-green-600/70 font-normal">{{ instructionLabel(item.instruction_type) }}</span>
                  </div>
                  <ArrowRight v-if="idx < previewResult.items.length - 1" class="w-4 h-4 text-gray-300 shrink-0" />
                </template>
              </div>
            </div>
          </template>
        </div>
      </template>
    </div>
  </AdminLayout>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import {
  ArrowLeft, ArrowRight, ChevronLeft, ChevronRight, Plus, Trash2, Copy,
  Workflow, GitBranch, Shield, Eye, Info,
} from 'lucide-vue-next'
import AdminLayout from '@/layouts/AdminLayout.vue'
import { apiFetch } from '@/utils/apiFetch'
import { useToast } from '@/utils/toast'
import {
  ROLE_NATIONAL_OPTIONS, INSTRUCTION_TYPE_OPTIONS, TARGET_TYPE_OPTIONS,
  emptyStep, type WorkflowStep, type WorkflowTemplate, type StepKind,
} from '@/types/workflowTemplate'

const router = useRouter()
const route = useRoute()
const toast = useToast()

const templateId = computed(() => {
  const id = route.params.id
  return id && id !== 'new' ? Number(id) : null
})

interface Direction { id: number; code: string; nom: string }
interface Departement { id: number; nom: string; direction: { id: number } }
interface ServiceRef { id: number; nom: string; departement: { id: number } | null }
interface Ambassade { id: number; code: string; nom: string }
interface UserOption { id: number; full_name: string; role_label: string }

const loading = ref(true)
const saving = ref(false)
const directions = ref<Direction[]>([])
const departements = ref<Departement[]>([])
const services = ref<ServiceRef[]>([])
const ambassades = ref<Ambassade[]>([])
const users = ref<UserOption[]>([])

const form = reactive({ nom: '', description: '', is_active: true })
let stepKeySeq = 0
const steps = ref<(WorkflowStep & { _key: number; _directionFilter?: number | null; _departementFilter?: number | null })[]>([])

const kindOptions: { value: StepKind; label: string }[] = [
  { value: 'FIXED_ENTITY', label: 'Cible précise' },
  { value: 'HIERARCHIE_INITIATEUR', label: 'Hiérarchie dynamique' },
  { value: 'ROLE_NATIONAL', label: 'Rôle national' },
]

const departementsForDirection = (directionId?: number | null) =>
  directionId ? departements.value.filter(d => d.direction?.id === directionId) : []

const servicesForDepartement = (departementId?: number | null) =>
  departementId ? services.value.filter(s => s.departement?.id === departementId) : []

const directionIdOfDepartement = (departementId?: number | null) =>
  departements.value.find(d => d.id === departementId)?.direction?.id ?? null

const directionIdOfService = (serviceId?: number | null) => {
  const svc = services.value.find(s => s.id === serviceId)
  return svc ? directionIdOfDepartement(svc.departement?.id) : null
}

const fetchReferenceData = async () => {
  const [dirs, depts, svcs, ambs, usersRaw] = await Promise.all([
    apiFetch<any[]>('/api/admin/directions/'),
    apiFetch<any[]>('/api/admin/departements/'),
    apiFetch<any[]>('/api/admin/services/'),
    apiFetch<any[]>('/api/admin/ambassades/'),
    apiFetch<any[]>('/api/admin/users/'),
  ])
  directions.value = dirs
  departements.value = depts
  services.value = svcs
  ambassades.value = ambs
  users.value = usersRaw
    .map((u: any) => ({
      id: u.id,
      full_name: u.full_name || u.username || `Utilisateur #${u.id}`,
      role_label: u.profile?.role?.nom || u.role?.nom || 'Sans rôle',
    }))
    .sort((a, b) => a.full_name.localeCompare(b.full_name))
}

const hydrateStep = (raw: WorkflowStep): typeof steps.value[number] => {
  const step: typeof steps.value[number] = {
    ...raw,
    _key: stepKeySeq++,
    target_id: raw.target?.id ?? null,
    target_type: raw.target?.type ?? raw.target_type ?? (raw.kind === 'FIXED_ENTITY' ? 'DIRECTION' : null),
    _directionFilter: null,
    _departementFilter: null,
  }
  if (step.kind === 'FIXED_ENTITY' && step.target_type === 'DEPARTEMENT') {
    step._directionFilter = directionIdOfDepartement(step.target_id)
  }
  if (step.kind === 'FIXED_ENTITY' && step.target_type === 'SERVICE') {
    step._directionFilter = directionIdOfService(step.target_id)
    step._departementFilter = services.value.find(s => s.id === step.target_id)?.departement?.id ?? null
  }
  return step
}

const loadTemplate = async () => {
  if (!templateId.value) {
    steps.value = [hydrateStep(emptyStep(1))]
    return
  }
  const wt = await apiFetch<WorkflowTemplate>(`/api/admin/workflow-templates/${templateId.value}/`)
  form.nom = wt.nom
  form.description = wt.description
  form.is_active = wt.is_active
  steps.value = wt.steps.map(hydrateStep)
}

onMounted(async () => {
  try {
    await fetchReferenceData()
    await loadTemplate()
  } catch (e: any) {
    toast.error('Erreur', e.message || 'Impossible de charger les données')
  } finally {
    loading.value = false
  }
})

// ─── Édition des étapes ─────────────────────────────────────────────────

const addStep = () => {
  steps.value.push(hydrateStep(emptyStep(steps.value.length + 1)))
}

const removeStep = (idx: number) => {
  steps.value.splice(idx, 1)
}

const moveStep = (idx: number, dir: -1 | 1) => {
  const target = idx + dir
  if (target < 0 || target >= steps.value.length) return
  const [item] = steps.value.splice(idx, 1)
  steps.value.splice(target, 0, item)
}

const setKind = (step: typeof steps.value[number], kind: StepKind) => {
  step.kind = kind
  if (kind === 'HIERARCHIE_INITIATEUR' && step.instruction_type === 'ACTION_CONJOINTE') {
    step.instruction_type = 'ACTION'
  }
  if (kind !== 'FIXED_ENTITY') {
    step.target_type = null
    step.target_id = null
  } else if (!step.target_type) {
    step.target_type = 'DIRECTION'
  }
  if (kind !== 'ROLE_NATIONAL') {
    step.role_national_code = null
  }
}

const onTargetTypeChange = (step: typeof steps.value[number]) => {
  step.target_id = null
  step._directionFilter = null
  step._departementFilter = null
}

// ─── Sauvegarde ──────────────────────────────────────────────────────────

const validate = (): string | null => {
  if (!form.nom.trim()) return 'Le nom est obligatoire'
  if (!steps.value.length) return 'Le workflow doit contenir au moins une étape'
  for (const [i, step] of steps.value.entries()) {
    const n = i + 1
    if (step.kind === 'FIXED_ENTITY' && !step.target_id) return `Étape ${n} : choisissez une cible`
    if (step.kind === 'ROLE_NATIONAL' && !step.role_national_code) return `Étape ${n} : choisissez un rôle national`
  }
  // La dernière étape clôt toujours le circuit (PKI), que "Signature" soit
  // coché ou non — mais seulement si elle exige au moins "Paraphe". Sans ça,
  // plus personne ne clôture jamais le circuit (piège de configuration
  // silencieux), re-vérifié côté serveur dans tous les cas.
  const derniere = steps.value[steps.value.length - 1]
  if (!derniere.requires_paraphe) {
    return 'La dernière étape doit garder "Paraphe" coché (que "Signature" le soit ou non) — sinon personne ne clôture jamais le circuit.'
  }
  return null
}

const buildPayload = () => ({
  nom: form.nom.trim(),
  description: form.description.trim(),
  is_active: form.is_active,
  steps: steps.value.map((step, idx) => ({
    ordre: idx + 1,
    kind: step.kind,
    target_type: step.kind === 'FIXED_ENTITY' ? step.target_type : undefined,
    target_id: step.kind === 'FIXED_ENTITY' ? step.target_id : undefined,
    role_national_code: step.kind === 'ROLE_NATIONAL' ? step.role_national_code : undefined,
    instruction_type: step.instruction_type,
    is_principal: step.is_principal,
    requires_paraphe: step.requires_paraphe,
    requires_signature: step.requires_signature,
  })),
})

const save = async () => {
  const error = validate()
  if (error) {
    toast.error('Validation', error)
    return
  }
  saving.value = true
  try {
    const payload = buildPayload()
    const saved = templateId.value
      ? await apiFetch<WorkflowTemplate>(`/api/admin/workflow-templates/${templateId.value}/`, {
          method: 'PUT', body: JSON.stringify(payload),
        })
      : await apiFetch<WorkflowTemplate>('/api/admin/workflow-templates/', {
          method: 'POST', body: JSON.stringify(payload),
        })

    toast.success('Workflow enregistré', `« ${saved.nom} » a été sauvegardé`)
    steps.value = saved.steps.map(hydrateStep)
    if (!templateId.value) {
      router.replace(`/workflow-templates/${saved.id}`)
    }
  } catch (e: any) {
    toast.error('Erreur', e.message || "Impossible d'enregistrer le workflow")
  } finally {
    saving.value = false
  }
}

const doDuplicate = async () => {
  if (!templateId.value) return
  try {
    const clone = await apiFetch<WorkflowTemplate>(`/api/admin/workflow-templates/${templateId.value}/duplicate/`, { method: 'POST' })
    toast.success('Workflow dupliqué', `« ${clone.nom} » a été créé`)
    router.push(`/workflow-templates/${clone.id}`)
  } catch (e: any) {
    toast.error('Erreur', e.message || 'Impossible de dupliquer ce workflow')
  }
}

// ─── Aperçu ──────────────────────────────────────────────────────────────

const previewUserId = ref<number | null>(null)
const previewing = ref(false)
const previewError = ref('')
const previewResult = ref<{ initiator: string; items: any[] } | null>(null)

const runPreview = async () => {
  if (!templateId.value || !previewUserId.value) return
  previewing.value = true
  previewError.value = ''
  previewResult.value = null
  try {
    previewResult.value = await apiFetch(
      `/api/admin/workflow-templates/${templateId.value}/preview/?user_id=${previewUserId.value}`
    )
  } catch (e: any) {
    previewError.value = e.message || "Impossible de résoudre le circuit pour cet utilisateur"
  } finally {
    previewing.value = false
  }
}

const instructionLabel = (value: string) =>
  INSTRUCTION_TYPE_OPTIONS.find(o => o.value === value)?.label || value
</script>
